import { Model, View } from "@lernetz/ts-lib";
import template from './Router.html';
import { ScrollHistory } from "./ScrollHistory";

export class RouterView extends View<Router> {

    constructor( config?) {
        super( { data: new Router(), template, ...config } );

        this.registry.add( undefined, () => new View( { template: '' } ) );
    }

    init() {

        // inject router to view context
        this.context.router = this.data;

        // listen to history back events
        window.addEventListener( 'popstate', this.data.onPopState.bind( this.data ) );

        // render current active path
        this.data.toCurrentPath();
    }

}


export interface IRoute<Content> {
    readonly name?: string;
    test( path: string ): boolean;
    invoke( path: string, router: Router<Content>, restoreScroll:boolean );
    pathOf( params: any ): string;
}

export interface DefaultRouterContent {
    view: any;
    data: any;
}


export class Router<RouterContent = DefaultRouterContent> extends Model {

    protected routes: IRoute<RouterContent>[];
    content: RouterContent;
    protected _current: {
        route: IRoute<RouterContent>,
        path: string,
    };
    readonly scrollHistory:ScrollHistory;
    mode: RouterMode;
    notFound: ( path:string ) => void;

    constructor( config?: { mode?: RouterMode, routes?: IRoute<RouterContent>[]; } ) {
        super( {
            content: { data: {} },
            _current: {},
            mode: new HistoryMode(),
            scrollHistory: new ScrollHistory(),
            routes: [],
            notFound: ( path ) => {
                console.log( 'Invalid path: ' + path );
            },
            ...config
        } );

    }


    /**
     * Navigates to the given path and shows the correspondoing content and pushes the path into the history
     * @param path the new path to go to
     */
    toPath( path: string, restoreScroll:boolean = false ) {

        // check for missing starting /
        if( path[ 0 ] != '/' ) path = '/' + path;

        // search over all routes for matches
        for( const route of this.routes ) {

            // check if current route matches
            if( route.test( path ) ) {

                // store old scrollpos
                this.scrollHistory.getPos( this._current.path );

                // set new current path
                this._current = { route, path };

                // invoke route action
                route.invoke( path, this, restoreScroll );

                // inform that the current path has changed
                this.propertyChange.dispatch( { name: 'current' } );

                // dont invoke another router after first match
                return;
            }
        }

        this.notFound( path );
    }

    /**
     * Navigates to a route with the given name and generates the path based on the given parameters
     * @param name The name of the route
     * @param params The parameters for the route
     */
    toRoute( name: string, params: any = {} ) {

        // search for route with name
        let found = this.routes.find( r => r.name == name );

        if( found ) this.toPath( found.pathOf( params ) );
    }

    /**
     * Returns the absolute url of a route
     * @param name The name of the route
     * @param params Any parameters for the route
     */
    urlOf( name:string, params: any = {} ) {

        // search for route with name
        let found = this.routes.find( r => r.name == name ) as IRoute<RouterContent>;

        if( !found ) return;

        return this.mode.getURL( found.pathOf( params ) );
    }



    /**
     * Callback when the browser navigates in the history states
     * @param e The event
     */
    onPopState( e: PopStateEvent ) {
        //The event happens after the new location has loaded
        // so we can simply load the current location
        this.toPath( this.mode.readURL(), true );
    }

    /**
     * Navigates to the current path in the URL
     */
    toCurrentPath() {
        this.toPath( this.mode.readURL() );
    }

    /**
     * Writes the given path in the URL without changing any views
     * @param path the new path to be written
     */
    writeURL( path: string ) {
        this.mode.writeURL( path );
    }

    /**
     * Reads the current path from the URL
     */
    readURL(): string {
        return this.mode.readURL();
    }

    /**
     * Adds a new Route definition
     * @param config
     */
    add( route:IRoute<RouterContent> ) {
        this.routes.push( route );
    }

    /**
     * Gets the current route/path object
     */
     get current():{ route: IRoute<RouterContent>, path: string } {
        return this._current;
    }
}






export interface RouterMode {
    /**
     * Updates the URL in the browser
     * @param path The new path to store in history
     */
    writeURL( path: string );


    /**
     * Reads the browser URL and returns the current path
     */
    readURL(): string;

    /**
     * Returns the full url of a path
     */
    getURL( path:string ):string;
}




export class HistoryMode implements RouterMode {

    base: string;
    query: string;
    hash: string;

    constructor( config?: { base: string; } ) {
        Object.assign( this, { base: window.location.origin, query: window.location.search, hash: window.location.hash }, config );
    }

    /**
     * Stores the new path into the browser history
     * @param path The new path to store/write
     */
    writeURL( path: string ) {
        let currentPath = window.location.href;
        let newPath = this.getURL( path );

        // only push if current path is different
        if( newPath != currentPath ) history.pushState( {}, '', newPath );
    }

    /**
     * Reads the browser url pathname and subtracts the base path
     */
    readURL() {
        // get url without query/hash
        let url = window.location.origin + window.location.pathname;
        return ( url.indexOf( this.base, 0 ) === 0 ) ? url.substr( this.base.length ) : url;
    }


    /**
     * Returns the full url of a path
     */
    getURL( path:string ) {
        return this.base + path + this.query + ( this.hash ? this.hash : '' );
    }
}

/**
 * Writes / reads the path from the URL hash
 */
export class HashMode implements RouterMode {

    writeURL( path: string ) {

        let currentPath = window.location.href;
        let newPath = this.getURL( path );

        // only push if current path is different
        if( newPath != currentPath ) history.pushState( {}, '', newPath );
    }

    readURL(): string {
        return window.location.hash.substring( 1 );
    }

    getURL(path: string): string {
        return window.location.href.split( "#" )[ 0 ] + "#" + path;
    }

}
